import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTemplates, useWorkspaces } from '../hooks/useClaw9';

/**
 * Panel for browsing and using templates
 */
function TemplateGalleryPanel({ className = '' }) {
    const { templates, categories, isLoading, error, fetchTemplates, deleteTemplate, refresh } = useTemplates();
    const { createWorkspace } = useWorkspaces({ autoFetch: false });

    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // Use workspace form
    const [showUseForm, setShowUseForm] = useState(false);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');

    const handleSearch = () => {
        fetchTemplates({
            category: selectedCategory || undefined,
            search: searchTerm || undefined
        });
    };

    const handleUseTemplate = async (e) => {
        e.preventDefault();
        const trimmedBotId = newWorkspaceName.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_');
        if (!selectedTemplate || !trimmedBotId) {
            setCreateError('Bot ID is required.');
            return;
        }

        setIsCreating(true);
        setCreateError(null);
        try {
            await createWorkspace({
                botId: trimmedBotId,
                templateId: selectedTemplate.template_id
            });
            setShowUseForm(false);
            setSelectedTemplate(null);
            setNewWorkspaceName('');
        } catch (err) {
            setCreateError(err.message);
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (templateId) => {
        try {
            await deleteTemplate(templateId);
            setDeleteConfirmId(null);
        } catch {
            // Error handled by hook
        }
    };

    const filteredTemplates = templates.filter(template => {
        if (selectedCategory && template.category !== selectedCategory) {
            return false;
        }
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            return (
                template.name?.toLowerCase().includes(search) ||
                template.description?.toLowerCase().includes(search) ||
                template.tags?.some(tag => tag.toLowerCase().includes(search))
            );
        }
        return true;
    });

    if (isLoading && templates.length === 0) {
        return (
            <div className={`flex items-center justify-center h-64 ${className}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Templates</h2>
                    <p className="text-sm text-gray-500">
                        {templates.length} template{templates.length !== 1 ? 's' : ''} available
                    </p>
                </div>
                <button
                    onClick={refresh}
                    disabled={isLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                    Refresh
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Filters */}
            <div className="mb-6 flex gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Search templates..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                    <option value="">All categories</option>
                    {categories.map(cat => (
                        <option key={cat.id || cat} value={cat.id || cat}>
                            {cat.name || cat}
                        </option>
                    ))}
                </select>
                <button
                    onClick={handleSearch}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                    Search
                </button>
            </div>

            {/* Template Grid */}
            {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
                    <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTemplates.map(template => (
                        <div
                            key={template.template_id}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                        >
                            {/* Template Header */}
                            <div className="p-4 border-b border-gray-100">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                            <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-900">
                                                {template.name}
                                            </h3>
                                            {template.category && (
                                                <span className="text-xs text-gray-500">
                                                    {template.category}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Template Body */}
                            <div className="p-4">
                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {template.description || 'No description available.'}
                                </p>

                                {/* Tags */}
                                {template.tags && template.tags.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {template.tags.slice(0, 3).map(tag => (
                                            <span
                                                key={tag}
                                                className="inline-flex px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                        {template.tags.length > 3 && (
                                            <span className="text-xs text-gray-400">
                                                +{template.tags.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Template Footer */}
                            <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
                                <button
                                    onClick={() => {
                                        setSelectedTemplate(template);
                                        setShowUseForm(true);
                                    }}
                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                >
                                    Use Template
                                </button>
                                {deleteConfirmId === template.template_id ? (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleDelete(template.template_id)}
                                            className="text-sm text-red-600 hover:text-red-800"
                                        >
                                            Confirm
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirmId(null)}
                                            className="text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setDeleteConfirmId(template.template_id)}
                                        className="text-sm text-gray-400 hover:text-red-600"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Use Template Modal */}
            {showUseForm && selectedTemplate && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            onClick={() => {
                                setShowUseForm(false);
                                setSelectedTemplate(null);
                                setCreateError(null);
                            }}
                        />

                        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-lg shadow-xl transform transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Create from Template
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowUseForm(false);
                                        setSelectedTemplate(null);
                                        setCreateError(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                                <p className="text-sm text-purple-800">
                                    Creating workspace from: <strong>{selectedTemplate.name}</strong>
                                </p>
                            </div>

                            <form onSubmit={handleUseTemplate}>
                                <div className="mb-4">
                                    <label htmlFor="new-ws-name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Bot ID
                                    </label>
                                    <input
                                        id="new-ws-name"
                                        type="text"
                                        value={newWorkspaceName}
                                        onChange={(e) => setNewWorkspaceName(e.target.value)}
                                        placeholder="e.g., my-sales-bot"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        disabled={isCreating}
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Unique identifier for your workspace. Use lowercase letters, numbers, dashes, and underscores.
                                    </p>
                                </div>

                                {createError && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                        <p className="text-sm text-red-600">{createError}</p>
                                    </div>
                                )}

                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowUseForm(false);
                                            setSelectedTemplate(null);
                                            setCreateError(null);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                        disabled={isCreating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                        disabled={isCreating}
                                    >
                                        {isCreating ? 'Creating...' : 'Create Workspace'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

TemplateGalleryPanel.propTypes = {
    className: PropTypes.string
};

export default TemplateGalleryPanel;
